import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import YearSelector from './YearSelector';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../utils/api';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Calendar, 
  FileText, 
  Building,
  AlertCircle,
  CheckCircle,
  Activity
} from 'lucide-react';

const KPIDashboard = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchKPIData();
    }
  }, [selectedCompany, currentYear]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data);
      if (response.data.length > 0 && !selectedCompany) {
        setSelectedCompany(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchKPIData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/kpi-dashboard/${selectedCompany}?year=${currentYear}`);
      setKpiData(response.data);
    } catch (error) {
      console.error('Error fetching KPI data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Dashboard KPI">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
        </div>
      </Layout>
    );
  }

  if (!kpiData) {
    return (
      <Layout title="Dashboard KPI">
        <Card className="p-6">
          <div className="text-center text-slate-500">Nessun dato disponibile</div>
        </Card>
      </Layout>
    );
  }

  const { booking_kpis, invoice_kpis, cash_flow_kpis, property_kpis, reconciliation_kpis, owner_payment_kpis, financial_health } = kpiData;

  // Prepare data for charts
  const collectionData = [
    { name: 'Incassato', value: booking_kpis.collected_amount },
    { name: 'Da Incassare', value: booking_kpis.to_collect_amount }
  ];

  const invoiceTypeData = [
    { name: 'Passive (Uscite)', value: invoice_kpis.total_passive_amount },
    { name: 'Attive (Entrate)', value: invoice_kpis.total_active_amount }
  ];

  const cashFlowMonthsData = [
    { name: 'Mesi Positivi', value: cash_flow_kpis.positive_months, fill: '#10b981' },
    { name: 'Mesi Negativi', value: cash_flow_kpis.negative_months, fill: '#f59e0b' },
    { name: 'Mesi Critici', value: cash_flow_kpis.critical_months, fill: '#ef4444' }
  ];

  const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#a855f7'];

  const getHealthBadge = () => {
    const score = financial_health.score;
    if (score >= 80) return { color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle };
    if (score >= 60) return { color: 'bg-blue-100 text-blue-800 border-blue-300', icon: Activity };
    if (score >= 40) return { color: 'bg-orange-100 text-orange-800 border-orange-300', icon: AlertCircle };
    return { color: 'bg-red-100 text-red-800 border-red-300', icon: AlertCircle };
  };

  const healthBadge = getHealthBadge();
  const HealthIcon = healthBadge.icon;

  return (
    <Layout title="Dashboard KPI Completa">
      <div className="space-y-6">
        {/* Header */}
        <Card className="p-6 border-0 shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">📊 Dashboard KPI Avanzata</h2>
              <p className="text-sm text-slate-600">Metriche aggregate da bookings, fatture, cash flow e riconciliazioni</p>
            </div>
          </div>
        </Card>

        {/* Company & Year Selector */}
        <Card className="p-4 border-0 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Azienda</label>
              <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(company => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Anno</label>
              <YearSelector currentYear={currentYear} onChange={setCurrentYear} />
            </div>
          </div>
        </Card>

        {/* Financial Health Score */}
        <Card className="p-6 border-0 shadow-lg bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">Punteggio Salute Finanziaria</h3>
              <p className="text-sm text-slate-600">Basato su cash flow, collezioni, occupazione e mesi critici</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-900 mb-2">{financial_health.score}</div>
              <Badge className={`${healthBadge.color} border px-4 py-2 text-lg`}>
                <HealthIcon className="h-5 w-5 mr-2" />
                {financial_health.rating}
              </Badge>
            </div>
          </div>
        </Card>

        {/* Booking KPIs */}
        <Card className="p-6 border-0 shadow-lg">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            KPI Prenotazioni
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="text-sm text-slate-600 mb-1">Totale Prenotazioni</div>
              <div className="text-2xl font-bold text-blue-900">{booking_kpis.total_bookings}</div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="text-sm text-slate-600 mb-1">Revenue Totale</div>
              <div className="text-2xl font-bold text-green-900">€{booking_kpis.total_revenue.toLocaleString()}</div>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <div className="text-sm text-slate-600 mb-1">Valore Medio</div>
              <div className="text-2xl font-bold text-purple-900">€{booking_kpis.avg_booking_value.toFixed(2)}</div>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg">
              <div className="text-sm text-slate-600 mb-1">Totale Notti</div>
              <div className="text-2xl font-bold text-orange-900">{booking_kpis.total_nights}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-slate-900 mb-3">Stato Incassi</h4>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={collectionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: €${value.toLocaleString()}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {collectionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `€${value.toLocaleString()}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded">
                <span className="text-sm text-slate-700">Incassato:</span>
                <span className="font-semibold text-green-900">€{booking_kpis.collected_amount.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded">
                <span className="text-sm text-slate-700">Da Incassare:</span>
                <span className="font-semibold text-orange-900">€{booking_kpis.to_collect_amount.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded">
                <span className="text-sm text-slate-700">Tasso Collezione:</span>
                <span className="font-semibold text-blue-900">{booking_kpis.collection_rate.toFixed(1)}%</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Invoice KPIs */}
        <Card className="p-6 border-0 shadow-lg">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-red-600" />
            KPI Fatture
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="text-sm text-slate-600 mb-1">Totale Fatture</div>
              <div className="text-2xl font-bold text-slate-900">{invoice_kpis.total_invoices}</div>
            </div>
            <div className="p-4 bg-red-50 rounded-lg">
              <div className="text-sm text-slate-600 mb-1">Fatture Passive</div>
              <div className="text-2xl font-bold text-red-900">{invoice_kpis.passive_invoices}</div>
              <div className="text-xs text-red-700 mt-1">€{invoice_kpis.total_passive_amount.toLocaleString()}</div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="text-sm text-slate-600 mb-1">Fatture Attive</div>
              <div className="text-2xl font-bold text-green-900">{invoice_kpis.active_invoices}</div>
              <div className="text-xs text-green-700 mt-1">€{invoice_kpis.total_active_amount.toLocaleString()}</div>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={invoiceTypeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => `€${value.toLocaleString()}`} />
              <Bar dataKey="value" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Cash Flow KPIs */}
        <Card className="p-6 border-0 shadow-lg">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            KPI Cash Flow
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="text-sm text-slate-600 mb-1">Entrate Totali</div>
              <div className="text-2xl font-bold text-green-900">€{cash_flow_kpis.total_entrate.toLocaleString()}</div>
            </div>
            <div className="p-4 bg-red-50 rounded-lg">
              <div className="text-sm text-slate-600 mb-1">Uscite Totali</div>
              <div className="text-2xl font-bold text-red-900">€{cash_flow_kpis.total_uscite.toLocaleString()}</div>
            </div>
            <div className={`p-4 rounded-lg ${cash_flow_kpis.risultato_netto >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
              <div className="text-sm text-slate-600 mb-1">Risultato Netto</div>
              <div className={`text-2xl font-bold flex items-center gap-2 ${cash_flow_kpis.risultato_netto >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                {cash_flow_kpis.risultato_netto >= 0 ? <TrendingUp className="h-6 w-6" /> : <TrendingDown className="h-6 w-6" />}
                €{cash_flow_kpis.risultato_netto.toLocaleString()}
              </div>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="text-sm text-slate-600 mb-1">Media Mensile Entrate</div>
              <div className="text-2xl font-bold text-blue-900">€{cash_flow_kpis.avg_monthly_entrate.toLocaleString()}</div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-slate-900 mb-3">Distribuzione Mesi per Stato</h4>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={cashFlowMonthsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#8884d8">
                  {cashFlowMonthsData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Property & Other KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="p-6 border-0 shadow-lg">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Building className="h-5 w-5 text-indigo-600" />
              KPI Immobili
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-indigo-50 rounded">
                <span className="text-sm text-slate-700">Totale Immobili:</span>
                <span className="font-semibold text-indigo-900">{property_kpis.total_properties}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-indigo-50 rounded">
                <span className="text-sm text-slate-700">Tasso Occupazione:</span>
                <span className="font-semibold text-indigo-900">{property_kpis.occupancy_rate.toFixed(1)}%</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-indigo-50 rounded">
                <span className="text-sm text-slate-700">Media Notti/Immobile:</span>
                <span className="font-semibold text-indigo-900">{property_kpis.avg_nights_per_property.toFixed(0)}</span>
              </div>
            </div>
          </Card>

          <Card className="p-6 border-0 shadow-lg">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Altre Metriche</h3>
            <div className="space-y-3">
              <div className="p-3 bg-purple-50 rounded">
                <div className="text-sm text-slate-700 mb-1">Voci Riconciliazione</div>
                <div className="font-semibold text-purple-900">{reconciliation_kpis.reconciliation_entries}</div>
                <div className="text-xs text-purple-700 mt-1">
                  Entrate: €{reconciliation_kpis.reconciliation_income.toLocaleString()} | 
                  Uscite: €{reconciliation_kpis.reconciliation_expense.toLocaleString()}
                </div>
              </div>
              <div className="p-3 bg-orange-50 rounded">
                <div className="text-sm text-slate-700 mb-1">Pagamenti Proprietari</div>
                <div className="font-semibold text-orange-900">
                  Pagati: €{owner_payment_kpis.paid_to_owners.toLocaleString()}
                </div>
                <div className="text-xs text-orange-700 mt-1">
                  Da Pagare: €{owner_payment_kpis.to_pay_owners.toLocaleString()}
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default KPIDashboard;
